/* COLOUR CARRIES TIER, AND THIS FILE WAS SPENDING IT ON A GATE (2026-08-30).
 *
 * Measured before this change, at 1512: the "continue" button on steps 2, 3 and 4 computed
 * `background: rgb(30,122,61)` — the `--verified` token itself — and the readiness panel around it
 * was `rgb(240,253,244)` with a `rgb(30,122,61)` border at 1452x167 (step 2) and 1452x163 (step 3).
 * Across all six screens of the two approved prototypes, ZERO elements paint rgb(30,122,61) as a
 * background, and the largest `verifiedBg` anywhere in them is a 163x27 chip. Ours was roughly
 * 8,900 times that area.
 *
 * That green means one thing in this product: this figure is confirmed against a named source.
 * Spending it on "you may now press continue" teaches the reader that the colour means "good",
 * which is exactly the reading the two-tag provenance model cannot afford. The gate is now a plain
 * surface with a hairline, and the action is the brand accent — the prototype's `.btn.p` navy.
 * Radius drops 8 -> 6 for the same reason: the prototype has three radii and 8px is not one.
 *
 * The CAUTION branch is untouched. An unmet requirement is a real warning and reads as one.
 */
import { useState, type ReactNode } from "react";
import { themeVar as colors } from "../design-tokens";
import { measure } from "../ui/measures";
import { SUBJECT_FIELD_SCHEMA, REQUIRED_FIELD_KEYS } from "./fieldSchema";
import { CONCESSION_BLOCK_PCT, CONCESSION_REVIEW_PCT, DEFAULT_PERSONA, isConcessionHumanConfirmed,
         PERSONAS, REPORT_TIERS, REPORT_TIER_LABELS, REPORT_TIER_HINTS,
         type PersonaId, type ReportTier } from "./constants";
import type { LoadedCounty, SubjectGrid } from "./types";
import { lotConflictBlockReason } from "./lotConflict";
import { useUsage } from "../lib/useUsage";
import { atUsageLimit, type Usage } from "../lib/usageApi";
import { LimitNoticeView, ServerLimitNotice, UsageLimitBannerView, UsageLineView } from "../ui/UsageWidgets";

/**
 * THE MONTHLY REPORT LIMIT (§6), READ ONCE PER GATE. Each of the three gates below calls
 * `useUsage()` itself rather than taking it as a prop — the same "leaf calls the hook" pattern
 * `ui/AppShell`/`home/AppHome` already use for `useSession`/`useModuleStatus` — so wiring this in
 * costs no change to every existing caller in `CmaAmbFlow.tsx`. `atUsageLimit` narrows `usage` to
 * non-null, so a limited render always has the figure it needs to print.
 *
 * WHICH BUTTONS ARE GATED, AND WHY NOT THE ALREADY-DISABLED ONES. The owner's refusal list names
 * county/CAD import, comp pulls and sheet uploads, and this product's real equivalents are the
 * "Fetch from public records" / "Upload…sheet" / "Add another sheet" controls below. The
 * "Generate the report" button in the two branches that are disabled for a DIFFERENT reason
 * (too few comps; a missing required field) is left alone — it is already inert and already says
 * why, and layering the limit reason on top of an unrelated blocker would answer a question
 * nobody asked yet. The one branch where Generate is actually live is where the limit is wired to
 * it.
 */
/**
 * ONE PRIMARY ACTION, PINNED AT A PHONE WIDTH (polish-primary-actions, #6). At 600px and under the
 * wizard's primary button was the last thing on a 2,000px page. This wrapper holds that single
 * button (and, on Generate, the one-line cost note and any limit reason) and `app.css` pins it to
 * the bottom of the glass below 600px. Above 600px it is `display: contents`: no box, so the
 * desktop layout is the markup it was before the wrapper existed.
 */
function StickyAction({ children }: { children: ReactNode }) {
  return <div className="vb-sticky-action">{children}</div>;
}

function gatedNotice(usage: Usage | null, limited: boolean, otherwiseDisabled: boolean | undefined) {
  return limited && !otherwiseDisabled && usage ? <LimitNoticeView usage={usage} /> : null;
}

/** VI-3 / guided-first-execution-brief §3c: "one readiness/completion gate before compute in
 * every path." A required field is one that is BOTH checked AND carries a value — an unchecked
 * row never counts, even if a stray value is sitting in the input (VI-3: "Save builds the subject
 * only from CHECKED, source-tagged fields"). */
export function isGridReady(grid: SubjectGrid): boolean {
  return REQUIRED_FIELD_KEYS.every((key) => grid[key]?.checked && grid[key]?.value != null && grid[key]?.value !== "");
}

/** THE FIELDS A CLOSED SALE MUST CARRY BEFORE IT CAN BE PRICED.
 *
 * This is a deliberate mirror of `engine.amb.validate_comp`'s `required` tuple for a closed sale
 * (`name, price, gla, lot_sf, year, garage`), minus `name`, which the grid always has and cannot
 * edit. It is duplicated here for ONE reason: so the disagreement surfaces in the grid, where the
 * agent can fix it, instead of at build time, where they cannot.
 *
 * `garage` is on this list and was, until 2026-08-04, not even a COLUMN. A comp missing it passed
 * every client check, was submitted, and was then silently set aside by `_prevalidate_inputs` — so
 * the report priced off fewer sales than the agent had selected, and nothing said so. The engine's
 * check stays exactly as harsh as it was; this one exists to make sure it never has to fire. */
export const COMP_REQUIRED_FIELDS = [
  { key: "price", label: "sold price" },
  { key: "gla", label: "GLA" },
  { key: "lot_sf", label: "lot size" },
  { key: "year", label: "year built" },
  { key: "garage", label: "garage bays" },
] as const;

export type CompFieldKey = (typeof COMP_REQUIRED_FIELDS)[number]["key"];

/** Which required fields this comp is still short, read from the LIVE row so typing a value in the
 * grid clears it on the very next render. Returns [] for a comp that is ready to price.
 *
 * `garage: 0` is a real, meaningful answer (a house with no garage), so this tests for null/blank
 * rather than for falsiness — a `!c.garage` here would demand the agent "fix" a correctly-read
 * zero and would refuse to accept the only truthful value. Price, GLA and lot must additionally be
 * POSITIVE, because the engine rejects a non-positive one outright. */
export function compMissingFields(c: Record<string, unknown>): CompFieldKey[] {
  return COMP_REQUIRED_FIELDS.filter(({ key }) => {
    const v = c[key];
    if (v == null || v === "") return true;
    const n = Number(v);
    if (Number.isNaN(n)) return true;
    if (key === "garage") return n < 0;
    return n <= 0;
  }).map(({ key }) => key);
}

/** A comp that is selected AND actually usable.
 *
 * 2026-07-31: a closed sale whose sheet never printed a sold price is now KEPT in the grid rather
 * than deleted (see `server/amb_extract._coerce`), so for the first time the comp list can contain
 * a row that is real, correct, and not yet valuable. `_selected` alone stopped being the same
 * question as "can this be built with". The engine agrees independently and more harshly:
 * `engine.amb.validate_comp` calls a missing price on a closed sale FATAL, so counting one here
 * would report a readiness the build then refuses — the agent clicks Generate and gets a halt.
 *
 * 2026-08-04: the same is now true of GLA, lot size, year and garage — `_coerce` keeps those rows
 * too rather than deleting them (an NTREIS export that prints a blank `Acres:` on half its records
 * is the measured case). So the question is no longer "does it have a price" but "is it short ANY
 * required field", and both are answered from the live row. */
export function isUsableComp(c: { _selected?: boolean; price?: number | null }): boolean {
  return !!c._selected && compMissingFields(c as Record<string, unknown>).length === 0;
}

/** A comp in the grid that is real and correctly read but waiting on something the agent can type.
 * The difference between "go find more comps" and "fill in three blanks" — two completely
 * different next actions, and telling the agent the wrong one is what sent the owner off to
 * re-upload the same export twice. */
export function isAwaitingInput(c: Record<string, unknown>): boolean {
  return compMissingFields(c).length > 0;
}

/** The distinct required fields missing across a set of comps, in the agent's words, as a phrase
 * ready to drop into a sentence. Named rather than counted, because "waiting on something" sends
 * the agent hunting across ten columns while "waiting on a lot size" sends them straight to it. */
export function missingFieldSummary(comps: Record<string, unknown>[]): string {
  const seen: string[] = [];
  for (const c of comps) {
    for (const key of compMissingFields(c)) {
      const label = COMP_REQUIRED_FIELDS.find((f) => f.key === key)?.label;
      if (label && !seen.includes(label)) seen.push(label);
    }
  }
  if (seen.length === 0) return "";
  // "a garage bays" (fixed 2026-08-11). The article was unconditional and every label but one is
  // singular, so it read correctly until the source cascade made the plural label — "garage bays",
  // the field public records carry no column for at all — the single most common thing this
  // sentence names. A plural takes no article.
  if (seen.length === 1) return /s$/.test(seen[0]) ? seen[0] : `a ${seen[0]}`;
  return `${seen.slice(0, -1).join(", ")} and ${seen[seen.length - 1]}`;
}

/** The engine's closed-comp floor, satisfied. Separate from `isGridReady` because the two failures
 * have different fixes: one is "confirm a field", the other is "select or upload more sales". */
export function hasEnoughComps(
  comps: { _selected?: boolean; price?: number | null }[],
  minComps?: number,
): boolean {
  return comps.filter(isUsableComp).length >= (minComps ?? 3);
}

/** The subject fields still missing, as schema defs (so the caller can name them). Shared by the
 * step-2 gate and the final gate rather than each re-deriving the same rule. */
export function missingRequiredFields(grid: SubjectGrid) {
  return SUBJECT_FIELD_SCHEMA.filter(
    (f) => f.required && !(grid[f.key]?.checked && grid[f.key]?.value != null && grid[f.key]?.value !== "")
  );
}

/**
 * STEP 2's gate — the subject's own facts (2026-08-01, wizard restructure).
 *
 * Two things this fixes that the old single-screen gate could not:
 *
 *  1. "FETCH FROM PUBLIC RECORDS" IS ALWAYS HERE. In the old gate it rendered only in the
 *     missing-fields branch, so it vanished the instant the subject was complete — invisible at
 *     exactly the moment an agent goes looking for it, which is when they want to check a figure
 *     they have rather than find one they don't. It is a permanent action on this step now.
 *  2. The blocker sits with the fields it is about, instead of below a comp table it has nothing
 *     to do with.
 */
/** WHY THE LOT SIZE IS BLOCKING when the sources disagree (2026-11-03). Renders nothing
 * for every other grid, so each gate is byte-identical to before when there is no conflict. */
function LotConflictReason({ grid }: { grid: SubjectGrid }) {
  const reason = lotConflictBlockReason(grid.lot_sf);
  if (!reason) return null;
  return (
    <p data-testid="lot-conflict-reason" role="status"
       style={{ marginTop: 6, fontSize: 13, color: colors.caution, fontWeight: 600, lineHeight: 1.5 }}>
      {reason}
    </p>
  );
}

/** The calm line that stands where "Fetch from public records" would be for a CAD-locked subject. */
function LockedRecordNote({ note }: { note: string }) {
  return (
    <p
      data-testid="subject-locked-note"
      style={{ margin: 0, alignSelf: "center", fontSize: 13, color: colors.textSecondary, lineHeight: 1.5, maxWidth: measure.copy }}
    >
      {note}
    </p>
  );
}

/**
 * "TYPE IT IN" (2026-10-18, owner code prompt Slice B). The grid above already takes a typed figure
 * (it tags it "You entered"), but on prod nothing said so: the banner's only action was "Upload
 * another sheet". This puts the cursor in the first missing required row and scrolls it into view.
 * Returns false when no such row is on the page, so the caller never ships a click that does
 * nothing (falls back to the first row of the grid).
 */
export function focusFirstMissingRow(keys: string[]): boolean {
  const field = (key: string) => document.querySelector<HTMLElement>(
    `[data-field-row="${key}"] input:not([type="checkbox"]), [data-field-row="${key}"] select`);
  const target = keys.map(field).find((el) => el != null)
    ?? document.querySelector<HTMLElement>(
      `[data-field-row] input:not([type="checkbox"]), [data-field-row] select`);
  if (!target) return false;
  target.scrollIntoView?.({ block: "center", behavior: "smooth" });
  target.focus({ preventScroll: true });
  return true;
}

export function SubjectStepGate({
  grid,
  onFetchPublicRecords,
  onAddFile,
  onContinue,
  fetchingPublicRecords,
  merging,
  limitRefusalMessage,
  lockedRecordNote,
}: {
  grid: SubjectGrid;
  onFetchPublicRecords: () => void;
  onAddFile: () => void;
  onContinue: () => void;
  /** Set when the subject is locked to a picked county CAD record: the sentence that replaces the
   * "Fetch from public records" control, which would otherwise call Cotality (2026-10-04). */
  lockedRecordNote?: string | null;
  fetchingPublicRecords: boolean;
  merging?: boolean;
  /** §6: the server's own `limit_reached` sentence when the last fetch/upload on THIS step came
   * back refused. See `ReadinessGate`'s own docstring on `gatedNotice` for the proactive half. */
  limitRefusalMessage?: string | null;
}) {
  const missing = missingRequiredFields(grid);
  const ready = missing.length === 0;
  const { usage } = useUsage();
  const limited = atUsageLimit(usage);
  return (
    <div
      style={{
        marginTop: 24,
        padding: 18,
        borderRadius: 10,
        background: ready ? colors.bgSurface : colors.cautionBg,
        border: `1px solid ${ready ? colors.border : colors.caution}`,
      }}
    >
      {/* §6, board 2: the quiet banner at the top of a step whose import/upload controls the
          monthly limit has paused. */}
      {limited && usage && <UsageLimitBannerView usage={usage} />}
      {/* §6: the SERVER's own refusal — the race case proactive disabling above cannot see. */}
      {limitRefusalMessage && <ServerLimitNotice message={limitRefusalMessage} />}
      {ready ? (
        <p style={{ margin: 0, fontSize: 14, color: colors.textPrimary, fontWeight: 600 }}>
          Every required field about this home is confirmed.
        </p>
      ) : (
        <>
          <p style={{ margin: 0, fontSize: 14, color: colors.caution, fontWeight: 600 }}>
            We couldn&apos;t confirm {missing.map((m) => m.label).join(" and ")} — the report
            can&apos;t compute without {missing.length > 1 ? "them" : "it"}.
          </p>
          <LotConflictReason grid={grid} />
          <p style={{ marginTop: 6, fontSize: 13, color: colors.textSecondary }}>
            If you know {missing.length > 1 ? "them" : "it"}, type {missing.length > 1 ? "them" : "it"} into
            the grid above. We tag what you type as &ldquo;You entered&rdquo;, and we never guess.
          </p>
        </>
      )}

      <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
        {/* THE FIRST ACTION IS THE ONE THAT ALWAYS WORKS (2026-10-18, Slice B). Typing a figure needs
            no lookup, no file and no allowance, so it leads; the sheet upload follows as the
            secondary choice. */}
        {!ready && (
          <button className="vb-btn"
            type="button"
            data-testid="type-it-in"
            onClick={() => focusFirstMissingRow(missing.map((m) => m.key))}
            style={{
              padding: "9px 16px", borderRadius: 8, border: "none",
              background: colors.accent, color: "#fff", fontWeight: 600, cursor: "pointer",
            }}
          >
            Type it in
          </button>
        )}
        {/* A SUBJECT LOCKED TO A COUNTY RECORD IS NOT SEARCHED AGAIN (2026-10-04, owner: "If the
            agent picked a CAD row, we never call Cotality at all"). The button would call it, so it
            is not offered; the line in its place says where the subject came from and why. */}
        {lockedRecordNote ? (
          <LockedRecordNote note={lockedRecordNote} />
        ) : (
          <button className="vb-btn"
            type="button"
            disabled={fetchingPublicRecords || merging || limited}
            onClick={onFetchPublicRecords}
            style={{
              padding: "9px 16px", borderRadius: 8, border: `1px solid ${colors.accent}`,
              background: colors.accentSoft, color: colors.accent,
              cursor: fetchingPublicRecords || merging || limited ? "not-allowed" : "pointer",
              opacity: merging || limited ? 0.6 : 1,
            }}
          >
            {fetchingPublicRecords ? "Searching public records…" : "Fetch from public records"}
          </button>
        )}
        <button className="vb-btn"
          type="button"
          onClick={onAddFile}
          disabled={merging || limited}
          aria-disabled={merging || limited}
          style={{
            padding: "9px 16px", borderRadius: 8, border: `1px solid ${colors.border}`,
            background: colors.bgSurface, color: colors.textPrimary,
            cursor: merging || limited ? "not-allowed" : "pointer", opacity: merging || limited ? 0.55 : 1,
          }}
        >
          Upload another sheet
        </button>
      </div>
      {/* §6: the per-control reason beside a control the monthly limit paused — not shown when
          the button is ALREADY disabled for a different, unrelated reason (a search in flight, a
          merge still running), so there is never more than one explanation for one dead button. */}
      {gatedNotice(usage, limited, fetchingPublicRecords || merging)}

      {merging && (
        <p role="status" aria-live="polite" style={{ marginTop: 12, fontSize: 13, color: colors.textSecondary }}>
          Reading the file you added — the rows above are still being merged.
        </p>
      )}

      {/* Disabled rather than hidden, the same rule the old gate used: the agent can see where the
          flow leads and why they cannot get there yet. */}
      <StickyAction>
<button className="vb-btn"
        type="button"
        onClick={onContinue}
        disabled={!ready || merging}
        aria-disabled={!ready || merging}
        title={ready ? undefined : "Confirm every required field above first"}
        style={{
          marginTop: 14, padding: "11px 20px", borderRadius: 6, border: "none",
          background: ready && !merging ? colors.accent : colors.neutralChip,
          color: ready && !merging ? "#fff" : colors.textSecondary,
          fontWeight: 600, cursor: ready && !merging ? "pointer" : "not-allowed",
        }}
      >
        Continue to the comparable sales
      </button>
</StickyAction>
    </div>
  );
}

/**
 * STEP 3's gate — the comparable set. Same two failure sentences the old gate carried (too few
 * selected vs. waiting on a sold price, which have completely different next actions), now sitting
 * directly under the table they describe rather than a full screen below it.
 */
export function CompsStepGate({
  comps,
  onAddFile,
  onContinue,
  merging,
  minComps,
  limitRefusalMessage,
}: {
  comps: {
    _selected?: boolean;
    price?: number | null;
    seller_concession?: number | null;
    name?: string | null;
    /** The two keys `engine.amb` reads as "a human confirmed this concession" — see
     * `isConcessionHumanConfirmed`. Optional, because a comp saved before the confirmation
     * contract existed carries neither, and an absent key means "not confirmed", which is the
     * safe reading. */
    seller_concession_source?: string | null;
    concession_human_confirmed?: boolean | null;
  }[];
  onAddFile: () => void;
  onContinue: () => void;
  merging?: boolean;
  minComps?: number;
  /** §6: the server's own `limit_reached` sentence when the last upload on THIS step came back
   * refused. */
  limitRefusalMessage?: string | null;
}) {
  const floor = minComps ?? 3;
  const selectedCount = comps.filter(isUsableComp).length;
  const enough = selectedCount >= floor;
  // WAITING ON ANY REQUIRED FIELD, not just a price (2026-08-04). This used to test the price
  // alone, so a row short its lot size fell into the `else` branch and the agent was told to "add
  // another sheet" while five fixable comps sat on the screen in front of them.
  const awaitingPrice = comps.filter((c) => isAwaitingInput(c as Record<string, unknown>));
  const awaitingWhat = missingFieldSummary(awaitingPrice as Record<string, unknown>[]);
  // The asking-price promise is a specific product commitment and is only true of a specific gap,
  // so it is printed only when the missing field is actually the sale price. Printing it beside a
  // missing garage count would be noise; dropping it where it belongs would quietly retire a
  // promise the product has made since the unpriced-comp work.
  const awaitingTheSoldPrice = awaitingPrice.some((c) => c.price == null || Number(c.price) <= 0);

  // A CONCESSION OVER 10% OF THE SALE PRICE STOPS THE BUILD (2026-08-04, owner directive).
  //
  // `Slr Paid` is now read as the seller-paid concession, because on an NTREIS closed sheet that
  // is where the dollars are printed while `Seller Concessions` often carries only "Yes". That
  // recovers real money the product was reporting as $0 — $5,000, $10,000 and $26,000 across
  // three sales on the owner's own file. It also re-opens the failure the old blanket refusal was
  // guarding: a settlement or proceeds figure landing in the same field, recognisable because it
  // approaches the sale price.
  //
  // The old answer was to ignore the field entirely, which threw away every real concession to
  // avoid a rare wrong one, and did it silently. This is the replacement: an implausible figure
  // is not dropped and not trusted — it stops the flow and is put in front of the person who can
  // settle it. A closing agent can key the wrong number into the MLS; that is exactly why the
  // cell stays editable, and why correcting it clears the block immediately.
  //
  // The threshold mirrors `engine.amb.CONCESSION_MAX_PCT`, and the server re-checks it on submit —
  // this gate is the explanation, never the enforcement.
  const overConcession = comps.filter((c) => {
    if (c._selected === false) return false;
    const p = typeof c.price === "number" ? c.price : null;
    const k = typeof c.seller_concession === "number" ? c.seller_concession : null;
    return p !== null && k !== null && p > 0 && k > 0 && k > p * CONCESSION_BLOCK_PCT;
  });
  const concessionBlocked = overConcession.length > 0;

  // THE 6–10% BAND IS NOT A HARD STOP, AND IT IS NOT NOTHING EITHER (2026-08-10).
  //
  // This gate has only ever spoken about concessions above 10%, the absolute bound. The band under
  // it is where the quiet damage happens: `engine.amb.concession_status` calls a SOURCE-extracted
  // figure above 6% `review_required`, and `screen_concessions` does not downweight such a
  // comparable — it removes it from the valuation entirely, then the build halts if the removals
  // leave fewer than `MIN_ARMS_LENGTH_COMPS` usable sales. On the owner's own Springtree file that
  // is 4404 Coldbrook Lane: $26,000 on a $358,000 sale, 7.26%, a real concession the sheet plainly
  // prints — and a comparable that silently leaves the report unless someone confirms it.
  //
  // A WARNING, NEVER A BLOCK. The 10% rule stops the flow because the figure is implausible and
  // has to be corrected before anything can be priced from it. This band is the opposite: the
  // figure is probably right, and all the engine wants is a person to say so. Blocking here would
  // be the product refusing to proceed over something it has no doubt about. The agent clears it
  // by retyping/confirming the number in the grid, which stamps `seller_concession_source='human'`
  // — and if they ignore it, Generate still works and the report still comes out honest, minus a
  // comparable it says it dropped.
  const needsConcessionConfirmation = comps.filter((c) => {
    if (c._selected === false) return false;
    if (isConcessionHumanConfirmed(c)) return false;
    const p = typeof c.price === "number" ? c.price : null;
    const k = typeof c.seller_concession === "number" ? c.seller_concession : null;
    if (p === null || k === null || p <= 0 || k <= 0) return false;
    return k > p * CONCESSION_REVIEW_PCT && k <= p * CONCESSION_BLOCK_PCT;
  });

  const { usage } = useUsage();
  const limited = atUsageLimit(usage);

  return (
    <div
      style={{
        marginTop: 24,
        padding: 18,
        borderRadius: 10,
        background: enough ? colors.bgSurface : colors.cautionBg,
        border: `1px solid ${enough ? colors.border : colors.caution}`,
      }}
    >
      {/* §6, board 2: the quiet banner at the top of a step whose upload control the monthly
          limit has paused. */}
      {limited && usage && <UsageLimitBannerView usage={usage} />}
      {limitRefusalMessage && <ServerLimitNotice message={limitRefusalMessage} />}
      {enough ? (
        <p style={{ margin: 0, fontSize: 14, color: colors.textPrimary, fontWeight: 600 }}>
          {selectedCount} closed {selectedCount === 1 ? "sale is" : "sales are"} selected — enough to
          price from.
        </p>
      ) : (
        <>
          <p style={{ margin: 0, fontSize: 14, color: colors.caution, fontWeight: 600 }}>
            {awaitingPrice.length > 0
              ? `${selectedCount} of the ${floor} closed sales a value needs ${selectedCount === 1 ? "is" : "are"} ready — ` +
                `${awaitingPrice.length} more ${awaitingPrice.length === 1 ? "is" : "are"} waiting on ${awaitingWhat}.`
              : selectedCount === 0
              ? `No comparable is selected — a value needs at least ${floor} closed sales.`
              : `Only ${selectedCount} comparable${selectedCount === 1 ? " is" : "s are"} selected — a value needs at least ${floor} closed sales.`}
          </p>
          <p style={{ marginTop: 6, fontSize: 13, color: colors.textSecondary }}>
            {awaitingPrice.length > 0
              /* "YOUR FILE" ONLY WHEN THERE IS ONE (2026-08-11). Since the source cascade an
                 agent can reach this screen having uploaded nothing at all — the comparables came
                 from the public record — and this sentence was blaming a file that does not exist.
                 The neutral wording is true of both routes and says the same thing. */
              ? `Those sales are in the grid above with everything else filled in — no source we reached states ${awaitingWhat} for them. Type it in the grid above, on the highlighted cell, and the sale goes straight back into the selected set; you don't have to tick the box as well. ` +
                (awaitingTheSoldPrice
                  ? "We won't guess it from the asking price."
                  : "Nothing is guessed to fill the gap.")
              : "Select more closed sales above, or add another sheet. Homes still on the market don't count toward this — an asking price is not a sale."}
          </p>
        </>
      )}

      <div style={{ marginTop: 12 }}>
        <button className="vb-btn"
          type="button"
          onClick={onAddFile}
          disabled={merging || limited}
          aria-disabled={merging || limited}
          style={{
            padding: "8px 14px", borderRadius: 8, border: `1px solid ${colors.border}`,
            background: colors.bgSurface, color: colors.textPrimary, fontSize: 13,
            cursor: merging || limited ? "not-allowed" : "pointer", opacity: merging || limited ? 0.55 : 1,
          }}
        >
          Add another sheet
        </button>
      </div>
      {gatedNotice(usage, limited, merging)}

      {merging && (
        <p role="status" aria-live="polite" style={{ marginTop: 12, fontSize: 13, color: colors.textSecondary }}>
          Reading the file you added — the rows above are still being merged.
        </p>
      )}

      {needsConcessionConfirmation.length > 0 && (
        <div
          role="status"
          style={{
            marginTop: 12, padding: "10px 12px", borderRadius: 8,
            background: colors.amberBg, border: `1px solid ${colors.amberLine}`,
          }}
        >
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: colors.amber }}>
            {needsConcessionConfirmation.length === 1
              ? "One selected sale has a seller concession above 6% of its sale price and nobody has confirmed it yet."
              : `${needsConcessionConfirmation.length} selected sales have seller concessions above 6% of their sale price and nobody has confirmed them yet.`}
          </p>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 13, color: colors.textSecondary, lineHeight: 1.6 }}>
            {needsConcessionConfirmation.map((c, i) => (
              <li key={i}>
                {c.name ?? "This sale"} — {typeof c.seller_concession === "number" ? `$${c.seller_concession.toLocaleString()}` : "—"}
                {typeof c.price === "number" ? ` on $${c.price.toLocaleString()}` : ""}
                {typeof c.price === "number" && typeof c.seller_concession === "number" && c.price > 0
                  ? ` (${((c.seller_concession / c.price) * 100).toFixed(1)}%)`
                  : ""}
              </li>
            ))}
          </ul>
          <p style={{ margin: "8px 0 0", fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
            A concession that size read straight from a file is not used until a person has looked
            at it: the valuation leaves {needsConcessionConfirmation.length === 1 ? "that sale" : "those sales"} out
            entirely rather than pricing a number nobody checked. Confirm the figure on the row
            above — retype it as itself if it is right — and{" "}
            {needsConcessionConfirmation.length === 1 ? "the sale stays" : "the sales stay"} in.
            You can carry on without doing it; the report will say what it left out and why.
          </p>
        </div>
      )}

      {concessionBlocked && (
        <div
          role="alert"
          style={{
            marginTop: 12, padding: "10px 12px", borderRadius: 8,
            background: colors.cautionBg, border: `1px solid ${colors.cautionLine}`,
          }}
        >
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: colors.caution }}>
            {overConcession.length === 1
              ? "One sale shows a seller concession over 10% of its own sale price."
              : `${overConcession.length} sales show a seller concession over 10% of their own sale price.`}
          </p>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 13, color: colors.textSecondary, lineHeight: 1.6 }}>
            {overConcession.map((c, i) => (
              <li key={i}>
                {c.name ?? "This sale"} — {typeof c.seller_concession === "number" ? `$${c.seller_concession.toLocaleString()}` : "—"}
                {typeof c.price === "number" ? ` on $${c.price.toLocaleString()}` : ""}
                {typeof c.price === "number" && typeof c.seller_concession === "number" && c.price > 0
                  ? ` (${Math.round((c.seller_concession / c.price) * 100)}%)`
                  : ""}
              </li>
            ))}
          </ul>
          <p style={{ margin: "8px 0 0", fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
            That is high enough that it is usually a mis-keyed closing entry, or a proceeds figure
            printed in the concession field. Correct it on the row above and this clears — or, if
            the sale really did carry it, type it again as itself and continue. We won&apos;t
            change it for you and we won&apos;t price it until you have looked.
          </p>
        </div>
      )}

      <StickyAction>
<button className="vb-btn"
        type="button"
        onClick={onContinue}
        disabled={!enough || concessionBlocked || merging}
        aria-disabled={!enough || concessionBlocked || merging}
        title={
          concessionBlocked
            ? "Check the seller concession flagged above first"
            : enough
            ? undefined
            : `Select at least ${floor} closed sales first`
        }
        style={{
          marginTop: 14, padding: "11px 20px", borderRadius: 6, border: "none",
          background: enough && !concessionBlocked && !merging ? colors.accent : colors.neutralChip,
          color: enough && !concessionBlocked && !merging ? "#fff" : colors.textSecondary,
          fontWeight: 600, cursor: enough && !concessionBlocked && !merging ? "pointer" : "not-allowed",
        }}
      >
        Continue to review
      </button>
</StickyAction>
    </div>
  );
}

export function ReadinessGate({
  grid,
  comps,
  onFetchPublicRecords,
  onAddFile,
  onSubmit,
  submitting,
  merging,
  fetchingPublicRecords,
  // `loadedCounties` / `selectedCounty` / `onSelectedCountyChange` stay in the PROP TYPE below but
  // are deliberately not destructured: the county picker was removed on 2026-08-01 (see the note
  // further down) and nothing in this component reads them any more. Keeping them in the contract
  // means `CmaAmbFlow` needs no change and re-introducing a county control later is a local edit.
  tier,
  onTierChange,
  personas,
  onPersonasChange,
  minComps,
  onFixSubject,
  onFixComps,
  limitRefusalMessage,
  lockedRecordNote,
}: {
  /** Set when the subject is locked to a picked county CAD record: the sentence that replaces the
   * "Fetch from public records" control, which would otherwise call Cotality (2026-10-04). */
  lockedRecordNote?: string | null;
  grid: SubjectGrid;
  comps: { _selected?: boolean; price?: number | null }[];
  onFetchPublicRecords: () => void;
  onAddFile: () => void;
  onSubmit: () => void;
  submitting: boolean;
  /** UI-086: an added-file merge is in flight. The indicator used to render ONLY when the grid was
   * not yet ready (`!isGridReady(grid) && busy`), so the most common case — a ready grid, agent
   * adds one more sheet — showed nothing at all, and "Generate" stayed live while the merge was
   * still rewriting the very rows it would submit. */
  merging?: boolean;
  fetchingPublicRecords: boolean;
  /** The REAL, currently-loaded (state, county) datasets (`GET /api/next/counties/loaded`) — the
   * picker below never lists a county this deployment doesn't actually have data for. */
  loadedCounties: LoadedCounty[];
  selectedCounty: LoadedCounty | null;
  onSelectedCountyChange: (c: LoadedCounty | null) => void;
  /** Session 8 — board doc §5 "AMB vs CMA: confirm both report tiers are reachable from the
   * guided flow." Previously hardcoded to "amb" in `CmaAmbFlow`; now a real choice at generate
   * time. */
  tier: ReportTier;
  onTierChange: (t: ReportTier) => void;
  /** Session 8 — board doc §3 "Item B": the 5 named narrative voices. Sent in the generate payload
   * regardless of backend readiness (harmless extra field today); see `PersonaPicker`'s own
   * docstring for why this ships now, disclosed, rather than waiting or hiding the control. */
  personas: PersonaId[];
  onPersonasChange: (p: PersonaId[]) => void;
  /** `ExtractGrid.min_comps` — the server's echo of `engine.amb.MIN_ARMS_LENGTH_COMPS`. Optional so
   * an older saved job still renders; falls back to the engine's shipped value of 3. */
  minComps?: number;
  /** Wizard restructure (2026-08-01). This gate is now the LAST step, so a problem it reports was
   * introduced on an earlier one — and the control that fixes it lives there, not here. When the
   * caller supplies these, each blocker offers a way back to the step that owns the fix instead of
   * duplicating that step's controls at the end of the flow. Optional on purpose: without them this
   * component renders exactly as it always has, which is what keeps every existing caller and test
   * honest. */
  onFixSubject?: () => void;
  onFixComps?: () => void;
  /** §6: the server's own `limit_reached` sentence when the last fetch/upload/Generate on THIS
   * step came back refused. */
  limitRefusalMessage?: string | null;
}) {
  // ONE SPELLING OF THE RULE (2026-08-05). This was a character-for-character copy of
  // `missingRequiredFields` above — the function that exists precisely so the step-2 gate and this
  // one cannot come to different conclusions about whether the same grid is ready. Two copies of a
  // readiness test is how a build gets blocked on one screen and armed on the next. The rule is
  // UNCHANGED: a required field still has to be both checked and carry a non-empty value.
  const missing = missingRequiredFields(grid);
  // THE COMP FLOOR, ENFORCED HERE RATHER THAN DISCOVERED SERVER-SIDE (2026-07-30).
  //
  // This used to warn only at ZERO selected and say "You can still submit". The engine halts below
  // MIN_ARMS_LENGTH_COMPS closed sales (`engine/amb.py`), so one or two selected comps produced a
  // confident "Generate", a full round trip, and then a failure — the same defect class as the
  // required-field gap above, and the one an MLS audience is most likely to walk into live.
  //
  // `minComps` is the SERVER's echo of the engine constant. The literal 3 is only a fallback for a
  // job payload saved before that field existed.
  const floor = minComps ?? 3;
  const selectedCount = comps.filter(isUsableComp).length;
  const enoughComps = selectedCount >= floor;
  const ready = missing.length === 0 && enoughComps;
  // Comps in the grid that are real and correctly read but waiting on a sale price the file never
  // printed. These are the difference between "you don't have enough comps" (go find more) and
  // "you have enough, type three numbers" — two completely different next actions, and telling the
  // agent the wrong one is what sent the owner off to re-upload the same export.
  // WAITING ON ANY REQUIRED FIELD, not just a price (2026-08-04). This used to test the price
  // alone, so a row short its lot size fell into the `else` branch and the agent was told to "add
  // another sheet" while five fixable comps sat on the screen in front of them.
  const awaitingPrice = comps.filter((c) => isAwaitingInput(c as Record<string, unknown>));
  const awaitingWhat = missingFieldSummary(awaitingPrice as Record<string, unknown>[]);
  // The asking-price promise is a specific product commitment and is only true of a specific gap,
  // so it is printed only when the missing field is actually the sale price. Printing it beside a
  // missing garage count would be noise; dropping it where it belongs would quietly retire a
  // promise the product has made since the unpriced-comp work.
  const awaitingTheSoldPrice = awaitingPrice.some((c) => c.price == null || Number(c.price) <= 0);

  const { usage } = useUsage();
  const limited = atUsageLimit(usage);

  // Missing fields and too-few comps are different problems with different fixes, so each gets its
  // own sentence rather than one merged "not ready".
  if (missing.length === 0 && !enoughComps) {
    return (
      <div style={{ marginTop: 24, padding: 18, borderRadius: 10, background: colors.cautionBg, border: `1px solid ${colors.cautionLine}` }}>
        {limitRefusalMessage && <ServerLimitNotice message={limitRefusalMessage} />}
        <p style={{ margin: 0, fontSize: 14, color: colors.caution, fontWeight: 600 }}>
          {/* THE COUNT IS ALWAYS STATED (2026-08-01). The awaiting-price branch used to report only
              how many sales were short a price and never how many were already selected, so an
              agent who had priced two of three saw a headline that had not moved and no way to tell
              they were one number away. Both facts are on the line now. */}
          {awaitingPrice.length > 0
            ? `${selectedCount} of the ${floor} closed sales a value needs ${selectedCount === 1 ? "is" : "are"} ready — ` +
              `${awaitingPrice.length} more ${awaitingPrice.length === 1 ? "is" : "are"} waiting on ${awaitingWhat}.`
            : selectedCount === 0
            ? `No comparable is selected — a value needs at least ${floor} closed sales.`
            : `Only ${selectedCount} comparable${selectedCount === 1 ? " is" : "s are"} selected — a value needs at least ${floor} closed sales.`}
        </p>
        {awaitingPrice.length > 0 ? (
          <p style={{ marginTop: 6, fontSize: 13, color: colors.textSecondary }}>
            {/* Source-neutral since the cascade (2026-08-11) — see the note in `CompsStepGate`. */}
            {awaitingPrice.length === 1 ? "That sale is" : "Those sales are"} listed as closed, and
            everything else on {awaitingPrice.length === 1 ? "it" : "them"} was read — no source we
            reached states {awaitingWhat}. Type it in the grid above and{" "}
            {awaitingPrice.length === 1 ? "that sale goes" : "those sales go"} straight back into the
            selected set, up to the {floor} the report needs — you don&apos;t have to tick the box
            as well.{" "}
            {awaitingTheSoldPrice
              ? "We won't guess it from the asking price."
              : "Nothing is guessed to fill the gap."}
          </p>
        ) : (
          <p style={{ marginTop: 6, fontSize: 13, color: colors.textSecondary }}>
            Select more closed sales in the grid above, or add another sheet. Homes still on the market
            don&apos;t count toward this — an asking price is not a sale.
          </p>
        )}
        <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
          {onFixComps && (
            <button className="vb-btn"
              type="button"
              onClick={onFixComps}
              style={{
                padding: "8px 14px", borderRadius: 8, border: "none",
                background: colors.accent, color: "#fff", fontSize: 13, fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Back to the comparable sales
            </button>
          )}
          <button className="vb-btn"
            type="button"
            onClick={onAddFile}
            disabled={merging || limited}
            aria-disabled={merging || limited}
            style={{
              padding: "8px 14px", borderRadius: 8, border: `1px solid ${colors.border}`,
              background: colors.bgSurface, color: colors.textPrimary, fontSize: 13,
              cursor: merging || limited ? "not-allowed" : "pointer", opacity: merging || limited ? 0.55 : 1,
            }}
          >
            Add another sheet
          </button>
        </div>
        {gatedNotice(usage, limited, merging)}
        {merging && (
          <p role="status" aria-live="polite" style={{ marginTop: 12, fontSize: 13, color: colors.textSecondary }}>
            Reading the file you added — the rows below are still being merged.
          </p>
        )}
        {/* Rendered DISABLED rather than hidden, matching the missing-fields branch below: the
            agent can see where the flow leads and why they cannot get there yet, instead of a
            button that silently appears once some invisible condition is met. Not re-captioned
            for the monthly limit even when `limited` is also true: this button is inert for a
            reason that has nothing to do with the limit (too few comps), and the limit's own
            reason is already said next to the control it actually disables above. */}
        <StickyAction>
<button className="vb-btn"
          type="button"
          disabled
          title={`Select at least ${floor} closed sales first`}
          style={{ marginTop: 14, padding: "11px 20px", borderRadius: 8, border: "none", background: colors.neutralChip, color: colors.textSecondary, cursor: "not-allowed" }}
        >
          Generate the report
        </button>
</StickyAction>
      </div>
    );
  }

  if (ready) {
    return (
      <div style={{ marginTop: 24, padding: 18, borderRadius: 10, background: colors.bgSurface, border: `1px solid ${colors.border}` }}>
        {limited && usage && <UsageLimitBannerView usage={usage} />}
        {limitRefusalMessage && <ServerLimitNotice message={limitRefusalMessage} />}
        <p style={{ margin: 0, fontSize: 14, color: colors.textPrimary, fontWeight: 600 }}>
          Every required field is confirmed, and {selectedCount} closed sales are selected.
        </p>

        <TierSummary tier={tier} onChange={onTierChange} />
        {/* UI-086: "add one more sheet" used to exist ONLY in the not-ready branch, so an agent
            with a complete grid had to break a required field to get the button back — which also
            made this branch's own merge indicator unreachable. It now sits under "More options"
            with the other voices (polish-primary-actions, #7): folded, never removed or disabled
            by the fold, and still counted in the fold's number. */}
        <PersonaPicker
          personas={personas}
          onChange={onPersonasChange}
          extraControls={[
            <button className="vb-btn"
              key="add-sheet"
              type="button"
              onClick={onAddFile}
              disabled={merging || limited}
              aria-disabled={merging || limited}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
                background: colors.bgSurface,
                color: colors.textPrimary,
                fontSize: 13,
                cursor: merging || limited ? "not-allowed" : "pointer",
                opacity: merging || limited ? 0.55 : 1,
              }}
            >
              Add another sheet
            </button>,
          ]}
        />
        {gatedNotice(usage, limited, merging)}

        {merging && (
          <p role="status" aria-live="polite" style={{ marginTop: 12, fontSize: 13, color: colors.textSecondary }}>
            Reading the file you added — the rows below are still being merged.
          </p>
        )}

        {/* §6, board 1 option C: the in-flow line above "Generate" — always shown once usage is
            known (not only at the limit), so the count is in view before the agent commits to the
            click, not only after it is refused. */}
        <StickyAction>
{usage && <UsageLineView usage={usage} action="generate" />}

        <button className="vb-btn"
          type="button"
          disabled={submitting || merging || limited}
          aria-disabled={submitting || merging || limited}
          onClick={onSubmit}
          style={{
            marginTop: 12,
            padding: "11px 20px",
            borderRadius: 6,
            border: "none",
            background: submitting || merging || limited ? colors.neutralChip : colors.accent,
            color: submitting || merging || limited ? colors.textSecondary : "#fff",
            fontWeight: 600,
            cursor: submitting || merging || limited ? "not-allowed" : "pointer",
          }}
        >
          {submitting ? "Submitting…" : merging ? "Waiting for the added file…" : "Generate the report"}
        </button>
        {gatedNotice(usage, limited, submitting || merging)}
</StickyAction>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 24, padding: 18, borderRadius: 10, background: colors.cautionBg, border: `1px solid ${colors.cautionLine}` }}>
      {limited && usage && <UsageLimitBannerView usage={usage} />}
      {limitRefusalMessage && <ServerLimitNotice message={limitRefusalMessage} />}
      <p style={{ margin: 0, fontSize: 14, color: colors.caution, fontWeight: 600 }}>
        We couldn&apos;t confirm {missing.map((m) => m.label).join(" and ")} — the report can&apos;t compute without{" "}
        {missing.length > 1 ? "them" : "it"}.
      </p>
      <LotConflictReason grid={grid} />
      <p style={{ marginTop: 6, fontSize: 13, color: colors.textSecondary }}>
        You can (1) fetch it from public records, (2) upload the county or MLS sheet, or (3) enter it directly in
        the grid above. We tag whatever you provide to its source — we won&apos;t guess.
      </p>

      {/* THE COUNTY PICKER IS GONE (2026-08-01, owner directive).
          It offered a choice that was never real: exactly one dataset is loaded in production
          (Collin), the control defaulted to `counties[0]` without ever reading the subject's ZIP,
          and the button was DISABLED unless a county was selected — so a national provider that
          covers every address in the country could only be reached through a dropdown about one
          Texas county. For a subject in Sachse, which straddles Dallas and Collin, that made the
          lookup structurally incapable of succeeding.

          `handleFetchPublicRecords` now goes to Cotality first and consults a county dataset only
          if that finds nothing, so this control has nothing left to decide. `loadedCounties` /
          `selectedCounty` stay in the props: the flow still passes a county through when one
          happens to be loaded, and removing them from the contract is a separate change. */}

      <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
        {onFixSubject && (
          <button className="vb-btn"
            type="button"
            onClick={onFixSubject}
            style={{
              padding: "9px 16px", borderRadius: 8, border: "none",
              background: colors.accent, color: "#fff", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Back to the property detail
          </button>
        )}
        {/* A SUBJECT LOCKED TO A COUNTY RECORD IS NOT SEARCHED AGAIN (2026-10-04, owner: "If the
            agent picked a CAD row, we never call Cotality at all"). The button would call it, so it
            is not offered; the line in its place says where the subject came from and why. */}
        {lockedRecordNote ? (
          <LockedRecordNote note={lockedRecordNote} />
        ) : (
          <button className="vb-btn"
            type="button"
            disabled={fetchingPublicRecords || merging || limited}
            onClick={onFetchPublicRecords}
            style={{
              padding: "9px 16px",
              borderRadius: 8,
              border: `1px solid ${colors.accent}`,
              background: colors.accentSoft,
              color: colors.accent,
              cursor: fetchingPublicRecords || merging || limited ? "not-allowed" : "pointer",
              opacity: merging || limited ? 0.6 : 1,
            }}
          >
            {fetchingPublicRecords ? "Searching public records…" : "Fetch from public records"}
          </button>
        )}
        <button className="vb-btn"
          type="button"
          onClick={onAddFile}
          disabled={merging || limited}
          aria-disabled={merging || limited}
          style={{
            padding: "9px 16px",
            borderRadius: 8,
            border: `1px solid ${colors.border}`,
            background: colors.bgSurface,
            color: colors.textPrimary,
            cursor: merging || limited ? "not-allowed" : "pointer",
            opacity: merging || limited ? 0.55 : 1,
          }}
        >
          Upload the subject&apos;s sheet
        </button>
      </div>
      {gatedNotice(usage, limited, fetchingPublicRecords || merging)}
      {merging && (
        <p role="status" aria-live="polite" style={{ marginTop: 12, fontSize: 13, color: colors.textSecondary }}>
          Reading the file you added — the rows above are still being merged.
        </p>
      )}
      <StickyAction>
<button className="vb-btn"
        type="button"
        disabled
        title="Complete every required field above first"
        style={{ marginTop: 14, padding: "11px 20px", borderRadius: 8, border: "none", background: colors.neutralChip, color: colors.textSecondary, cursor: "not-allowed" }}
      >
        Generate the report
      </button>
</StickyAction>
    </div>
  );
}

/**
 * UI-052 — the tier CHOICE now lives on the intake screen, where `tier` is actually consumed
 * (`startExtract` sends it). What remains here is a one-line confirmation of what is about to be
 * generated, plus an in-place way to change it — not a link back to a screen the flow has left.
 */
function TierSummary({ tier, onChange }: { tier: ReportTier; onChange: (t: ReportTier) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 14 }}>
      <p style={{ margin: 0, fontSize: 13, color: colors.textPrimary }}>
        {REPORT_TIER_LABELS[tier]}{" "}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          style={{ border: "none", background: "transparent", color: colors.accent, fontSize: 13, cursor: "pointer", padding: "0 2px", textDecoration: "underline" }}
        >
          {open ? "keep this" : "change"}
        </button>
      </p>
      {open && (
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          {REPORT_TIERS.map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={t === tier}
              onClick={() => {
                onChange(t);
                setOpen(false);
              }}
              style={{
                padding: "7px 12px",
                borderRadius: 8,
                /* A CHOICE, NOT AN EVIDENCE CLAIM (2026-08-30) — see the file header. The
                   selected tier was drawn in the VERIFIED tier's own two colours; it is the brand
                   accent now, matching `.typeOpt.on` in the port and the report screen's picker. */
                border: `1px solid ${t === tier ? colors.accent : colors.border}`,
                background: t === tier ? colors.accentSoft : colors.bgSurface,
                color: t === tier ? colors.accent : colors.textPrimary,
                fontWeight: t === tier ? 600 : 400,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              {REPORT_TIER_LABELS[t]}
            </button>
          ))}
        </div>
      )}
      <p style={{ marginTop: 4, fontSize: 11, color: colors.textSecondary }}>{REPORT_TIER_HINTS[tier]}</p>
    </div>
  );
}

/**
 * The 5 named narrative-voice picker (board doc `06_OWNER_DIRECTION_editable-personas-scope.md`
 * §3 "Item B" — owner-final ids). The flagship (`house_blend`, shown as "The Blend" since
 * 2026-08-05) is preselected per that doc. Every name and descriptor rendered here comes from
 * `constants.PERSONAS`, which is a hardcoded copy of the server's registry — see that table's
 * comment for why the copy exists and what it costs.
 *
 * ALL FIVE ARE SELECTABLE (2026-07-29). Until now four of them were rendered disabled and labelled
 * "· soon", under a note telling the user they did not change the report. That note was describing
 * a backend limitation that had already been fixed: `ai_narrative` accepts `persona`, and
 * `amb_narrative_spec.PERSONAS` carries a distinct voice body for every id here. The picker was
 * suppressing a feature the product already shipped.
 */
function PersonaPicker({ personas, onChange, extraControls = [] }: {
  personas: PersonaId[];
  onChange: (p: PersonaId[]) => void;
  /** Other controls to keep under the same counted fold (each counts once). */
  extraControls?: ReactNode[];
}) {
  const chosen = new Set(personas);
  const all = chosen.size === PERSONAS.length;
  const [foldOpen, setFoldOpen] = useState(false);
  // The fold may never hide a choice the agent has made: any selection other than the lone default
  // keeps it open, so "which voices will be built" is always on screen.
  const nonDefault = !(chosen.size === 1 && chosen.has(DEFAULT_PERSONA));
  // Counted from what is inside it, not hard-coded: the other voices, "Generate all five", and each
  // extra control passed in.
  const foldedVoices = PERSONAS.filter((p) => p.id !== DEFAULT_PERSONA);
  const foldCount = foldedVoices.length + 1 + extraControls.length;
  const voiceButton = (p: (typeof PERSONAS)[number]) => {
    const selected = chosen.has(p.id);
    return (
      <button
        key={p.id}
        type="button"
        aria-pressed={selected}
        onClick={() => toggle(p.id)}
        title={p.descriptor}
        style={{
          padding: "7px 12px",
          borderRadius: 8,
          border: `1px solid ${selected ? colors.accent : colors.border}`,
          background: selected ? colors.accentSoft : colors.bgSurface,
          color: selected ? colors.accent : colors.textPrimary,
          fontWeight: selected ? 600 : 400,
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        {p.name}
      </button>
    );
  };

  /* MULTI-SELECT, FOR TUNING (2026-08-04, owner directive).
   *
   * "we also need a button to generate a file for all of the narrative personas. if an agent wants
   * to see how the different report narratives compare then we need to allow them to select 1 or
   * all narratives to have a file created for. I understand the token risk but we need this
   * capability early when tuning these."
   *
   * The cost is real and is stated on screen rather than buried: each voice is a separate build,
   * so five voices is five model calls and five sets of deliverables. An agent choosing that
   * should know they chose it.
   *
   * ONE VOICE STAYS THE DEFAULT and behaves exactly as before — same single job, same inline
   * report. Nothing about the ordinary path changes. */
  function toggle(id: PersonaId) {
    const next = new Set(chosen);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    // Never allow an empty set: a report has to be written in some voice, and an empty picker
    // would arm Generate with nothing to generate.
    onChange(next.size ? (PERSONAS.map((p) => p.id).filter((id2) => next.has(id2)) as PersonaId[])
                       : [DEFAULT_PERSONA]);
  }

  return (
    <div style={{ marginTop: 14 }}>
      <label style={{ fontSize: 12, color: colors.textSecondary, fontWeight: 600 }}>Narrative voice</label>
      {/* UI-079 is resolved by the voices being real, not by a disclosure: all five change the
          prose. The line below says what the choice actually does, rather than apologising for it. */}
      <p style={{ marginTop: 6, marginBottom: 6, fontSize: 11, color: colors.textSecondary }}>
        Each voice rewrites the brief in a different register. The figures never change — only the
        writing.
      </p>
      <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
        {PERSONAS.filter((p) => p.id === DEFAULT_PERSONA).map(voiceButton)}
      </div>
      {/* MORE OPTIONS (N) — polish-primary-actions, #7. Only the wrapper is new: the buttons inside
          are the same elements with the same handlers, present in the DOM and enabled whether the
          fold is open or shut. */}
      <details
        open={foldOpen || nonDefault}
        onToggle={(e) => setFoldOpen((e.currentTarget as HTMLDetailsElement).open)}
        style={{ marginTop: 10 }}
      >
        <summary style={{ cursor: "pointer", fontSize: 13, color: colors.accent }}>
          More options ({foldCount})
        </summary>
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
          {foldedVoices.map(voiceButton)}
        {/* ONE ROW, NOT TWO (2026-09-01, W3-UIFIX2). From
            `w2-uifix/after/step4-review@1920.png`: the five voices sat on one line ending at
            x≈954, and this control — which selects among exactly those five — sat alone on a
            second line beneath them with ~590px of empty card to its right. It read as an orphan
            because it was on a row of its own, not because it lacked room. It is a control over
            the same set, so it belongs on the same row; the separator and the gap keep it from
            reading as a sixth voice, and it stays a plain button while the voices are toggles with
            `aria-pressed`, so nothing about what it is is carried by position alone. Below ~700px
            of card the row wraps as it always did. */}
        <span aria-hidden="true" style={{ width: 1, alignSelf: "stretch", margin: "2px 4px", background: colors.border }} />
        <button
          type="button"
          onClick={() => onChange(all ? [DEFAULT_PERSONA] : (PERSONAS.map((p) => p.id) as PersonaId[]))}
          style={{
            padding: "6px 11px", borderRadius: 8, border: `1px solid ${colors.border}`,
            background: colors.bgSurface, color: colors.textPrimary, fontSize: 12, cursor: "pointer",
          }}
        >
          {all ? "Just one voice" : "Generate all five"}
        </button>
        {chosen.size > 1 && (
          <span style={{ fontSize: 11, color: colors.caution }}>
            {chosen.size} voices — {chosen.size} separate builds, and {chosen.size * 2} files.
          </span>
        )}
        </div>
        {extraControls.length > 0 && (
          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>{extraControls}</div>
        )}
      </details>
      <p style={{ marginTop: 8, fontSize: 11, color: colors.textSecondary, lineHeight: 1.5 }}>
        {chosen.size === 1
          ? PERSONAS.find((p) => chosen.has(p.id))?.descriptor
          : "Each voice is written and rendered on its own, from the same confirmed figures — nothing is shared between them. When more than one is selected the report is not shown on screen; you download the set and compare them side by side."}
      </p>
    </div>
  );
}
